import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BUG_ENVIRONMENTS,
  BUG_PRIORITIES,
  BUG_SEVERITIES,
  BUG_TYPES,
  createBug,
} from "../services/bugService";
import { getUsersSummary } from "../services/userService";
import { useAuth } from "../hooks/useAuth";
import { routePaths } from "../constants/routes";
import "../style/bugTracker.css";

const initialForm = {
  project: "QA Coder",
  module: "",
  feature: "",
  title: "",
  type: "Bug",
  priority: "Medium",
  severity: "Major",
  environment: "QA",
  browserDevice: "",
  os: "",
  buildVersion: "",
  description: "",
  precondition: "",
  stepsText: "",
  expectedResult: "",
  actualResult: "",
  assignedToId: "",
  assignedToName: "",
  labelsText: "",
  dueDate: "",
};

export default function RaiseBug() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [form, setForm] = useState(initialForm);
  const [assignees, setAssignees] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const canAssign = ["admin", "trainer"].includes(role);

  useEffect(() => {
    getUsersSummary()
      .then((summary) => {
        setAssignees(
          summary.users
            .filter((candidate) => ["admin", "trainer"].includes(candidate.role))
            .map((candidate) => ({
              id: candidate.id,
              name: candidate.fullName || candidate.displayName || candidate.email || candidate.id,
            }))
        );
      })
      .catch((error) => console.warn("Unable to load bug assignees:", error));
  }, []);

  const selectedFiles = useMemo(() => Array.from(files), [files]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "assignedToId") {
      const selectedAssignee = assignees.find((assignee) => assignee.id === value);
      setForm((currentForm) => ({
        ...currentForm,
        assignedToId: value,
        assignedToName: selectedAssignee?.name || "",
      }));
      return;
    }

    setForm((currentForm) => ({ ...currentForm, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.title.trim() || !form.module.trim() || !form.description.trim()) {
      toast.error("Please enter title, module, and description.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        module: form.module.trim(),
        labels: form.labelsText
          .split(",")
          .map((label) => label.trim())
          .filter(Boolean),
        stepsToReproduce: form.stepsText
          .split("\n")
          .map((step) => step.trim())
          .filter(Boolean),
      };

      delete payload.labelsText;
      delete payload.stepsText;

      const result = await createBug(payload, selectedFiles, user);
      toast.success(`${result.bugId} created successfully.`);
      navigate(routePaths.bugDetail(result.id));
    } catch (error) {
      console.error("Unable to create bug:", error);
      toast.error("Unable to create bug. Please check Firestore permissions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="bug-page">
      <section className="bug-hero">
        <div>
          <span className="bug-eyebrow">Raise a Bug</span>
          <h1>Bug Report Template</h1>
          <p>Capture complete defect details, priority, severity, environment, proof, and ownership.</p>
        </div>
        <button type="button" className="bug-secondary-action" onClick={() => setForm(initialForm)}>
          Reset
        </button>
      </section>

      <form className="bug-form" onSubmit={handleSubmit}>
        <section className="bug-form-main bug-panel">
          <h2>Basic Details</h2>
          <div className="bug-form-grid two">
            <label>Project / Module<input name="module" value={form.module} onChange={handleChange} placeholder="Authentication, Payments..." required /></label>
            <label>Feature<input name="feature" value={form.feature} onChange={handleChange} placeholder="Login, Cart, Dashboard..." /></label>
          </div>
          <label>Bug Title / Summary<input name="title" value={form.title} onChange={handleChange} placeholder="Login button not working on mobile" required /></label>
          <label>Bug Type<select name="type" value={form.type} onChange={handleChange}>{BUG_TYPES.map((type) => <option key={type}>{type}</option>)}</select></label>

          <h2>Bug Description</h2>
          <label>Description<textarea name="description" value={form.description} onChange={handleChange} rows="4" required /></label>
          <label>Precondition<textarea name="precondition" value={form.precondition} onChange={handleChange} rows="3" /></label>
          <label>Steps to Reproduce<textarea name="stepsText" value={form.stepsText} onChange={handleChange} rows="6" placeholder={"Open login page\nEnter valid email and password\nClick login button"} /></label>
          <div className="bug-form-grid two">
            <label>Expected Result<textarea name="expectedResult" value={form.expectedResult} onChange={handleChange} rows="4" /></label>
            <label>Actual Result<textarea name="actualResult" value={form.actualResult} onChange={handleChange} rows="4" /></label>
          </div>
        </section>

        <aside className="bug-form-side bug-panel">
          <h2>Priority & Severity</h2>
          <label>Priority<select name="priority" value={form.priority} onChange={handleChange}>{BUG_PRIORITIES.map((priority) => <option key={priority}>{priority}</option>)}</select></label>
          <label>Severity<select name="severity" value={form.severity} onChange={handleChange}>{BUG_SEVERITIES.map((severity) => <option key={severity}>{severity}</option>)}</select></label>

          <h2>Environment Details</h2>
          <label>Environment<select name="environment" value={form.environment} onChange={handleChange}>{BUG_ENVIRONMENTS.map((environment) => <option key={environment}>{environment}</option>)}</select></label>
          <label>Browser / Device<input name="browserDevice" value={form.browserDevice} onChange={handleChange} placeholder="Chrome, iPhone 15, Android" /></label>
          <label>OS<input name="os" value={form.os} onChange={handleChange} placeholder="Windows, macOS, Android" /></label>
          <label>Build Version / Release Version<input name="buildVersion" value={form.buildVersion} onChange={handleChange} placeholder="v1.2.0" /></label>

          <h2>Additional Details</h2>
          <label>Assigned To<select name="assignedToId" value={form.assignedToId} onChange={handleChange} disabled={!canAssign}><option value="">Unassigned</option>{assignees.map((assignee) => <option value={assignee.id} key={assignee.id}>{assignee.name}</option>)}</select></label>
          <label>Reported By<input value={user?.email || ""} readOnly /></label>
          <label>Labels / Tags<input name="labelsText" value={form.labelsText} onChange={handleChange} placeholder="login, mobile, regression" /></label>
          <label>Due Date<input type="date" name="dueDate" value={form.dueDate} onChange={handleChange} /></label>

          <h2>Attachment Upload</h2>
          <label className="bug-upload">Screenshot, video, logs, document<input type="file" multiple accept=".png,.jpg,.jpeg,.mp4,.pdf,.log,.txt,.doc,.docx" onChange={(event) => setFiles(event.target.files)} /></label>
          {selectedFiles.length > 0 && (
            <ul className="bug-file-list">
              {selectedFiles.map((file) => <li key={`${file.name}-${file.size}`}>{file.name}</li>)}
            </ul>
          )}

          <div className="bug-form-actions">
            <button type="button" className="bug-secondary-action" onClick={() => navigate(routePaths.bugs)}>
              Save Draft
            </button>
            <button type="submit" className="bug-primary-action" disabled={saving}>
              {saving ? "Submitting..." : "Submit Bug"}
            </button>
          </div>
        </aside>
      </form>
    </main>
  );
}
